import { HttpErrorResponse } from "@angular/common/http";
import { computed, Inject, Injectable, signal } from "@angular/core";

import * as i18nIsoCountries from "i18n-iso-countries";

import { LanguageEnum } from "@shared/enums";
import { APP_CONFIG } from "@shared/tokens";

import { ApiService } from "./api.service";
import { IBaseUnit, IConfig, ISelectOption } from "../../interfaces";
import { LocalizationService } from "../localization.service";

@Injectable({
  providedIn: "root",
})
export class CommonService {
  private readonly baseUrl: string = `${this.environment.baseUrl}common/`;

  private allCountriesOptions = signal<ISelectOption[]>([]);

  private allSystemUnitsOfMeasurements = signal<IBaseUnit[]>([]);

  public readonly countriesOptions = computed(() => this.allCountriesOptions());

  public readonly systemUnitsOfMeasurements = computed(() => this.allSystemUnitsOfMeasurements());

  constructor(
    private apiService: ApiService,
    private localizationService: LocalizationService,
    @Inject(APP_CONFIG) private environment: IConfig,
  ) {}

  public loadSystemUnitsOfMeasurements = async (): Promise<void> => {
    if (this.allSystemUnitsOfMeasurements().length) {
      return;
    }

    await this.apiService
      .get<IBaseUnit[]>(`${this.baseUrl}base-units`)
      .toPromise()
      .then((response: IBaseUnit[]) => {
        if (response?.length) {
          this.allSystemUnitsOfMeasurements.set(response);
        }
      });
  };

  public loadCountries = async (): Promise<void> => {
    if (this.allCountriesOptions().length) {
      return;
    }

    await this.apiService
      .get<string[]>(`${this.baseUrl}countries`)
      .toPromise()
      .then(async (countryCodes: string[]) => {
        if (countryCodes?.length) {
          this.allCountriesOptions.set(
            countryCodes
              .filter((countryCode: string) => !!this.getCountryName(countryCode))
              .map((countryCode: string) => ({
                label: this.getCountryName(countryCode),
                value: countryCode,
              }))
              .sort((a, b) => a.label.localeCompare(b.label)),
          );
        }
      })
      .catch((error: HttpErrorResponse) => {
        console.error(error);
      });
  };

  public getCountryName = (
    countryCode: string,
    language: LanguageEnum = this.localizationService.getLanguage(),
  ): string => {
    const countryName = i18nIsoCountries.getName(countryCode, language);

    return countryName ?? countryCode;
  };
}
